<template>
  <div class="animated fadeIn">
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="icon-chart mr-2 text-success"></i>Live Monitoring</h4>
        <small class="text-muted">
          Last refreshed: <strong>{{ lastRefreshed || '—' }}</strong>
        </small>
      </b-col>
      <b-col class="text-right">
        <b-badge :variant="countdown <= 10 ? 'warning' : 'secondary'" class="mr-2">
          Refresh in {{ countdown }}s
        </b-badge>
        <b-button size="sm" variant="outline-primary" @click="load" :disabled="loading">
          <i class="icon-refresh"></i> Refresh Now
        </b-button>
      </b-col>
    </b-row>

    <!-- Status Cards -->
    <b-row class="mb-4">
      <b-col md="4" v-for="svc in services" :key="svc.service_name" class="mb-3">
        <b-card :class="'border-' + badgeVariant(svc.status)" class="status-card text-center h-100">
          <div class="status-icon mb-2" :class="'text-' + badgeVariant(svc.status)">
            <i :class="statusIcon(svc.status)" style="font-size:2rem"></i>
          </div>
          <h5 class="mb-1">{{ svc.service_name }}</h5>
          <b-badge :variant="badgeVariant(svc.status)" class="mb-2" style="font-size:.9rem;padding:.35em .75em">
            {{ svc.status }}
          </b-badge>
          <div class="text-muted small mt-1">
            <span v-if="svc.response_time_ms != null">
              <i class="icon-clock mr-1"></i>{{ svc.response_time_ms }} ms
            </span>
            <span v-else class="text-danger">No response</span>
          </div>
          <div class="text-muted mt-1" style="font-size:.72rem">{{ svc.checked_at | fmtdt }}</div>
        </b-card>
      </b-col>
      <b-col md="4" v-if="services.length === 0 && !loading" class="mb-3" v-for="n in 3" :key="'sk'+n">
        <b-card class="text-center h-100 bg-light"><b-spinner small /> Loading...</b-card>
      </b-col>
    </b-row>

    <!-- Trend Chart -->
    <b-card class="mb-4" no-body>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <span><i class="icon-graph mr-1"></i>Response Time Trend — Last 24 h</span>
        <b-spinner v-if="loading" small variant="secondary" />
      </b-card-header>
      <b-card-body>
        <canvas ref="chart" height="80"></canvas>
      </b-card-body>
    </b-card>

    <!-- Recent Events -->
    <b-card no-body>
      <b-card-header><i class="icon-list mr-1"></i>Last 10 Events</b-card-header>
      <b-table
        :items="recentEvents"
        :fields="fields"
        :busy="loading"
        responsive striped hover show-empty
        empty-text="No events recorded yet."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center py-3"><b-spinner variant="primary" /> Loading…</div>
        </template>
        <template #cell(status)="d">
          <b-badge :variant="badgeVariant(d.value)">{{ d.value }}</b-badge>
        </template>
        <template #cell(response_time_ms)="d">
          <span v-if="d.value != null">{{ d.value }} ms</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(checked_at)="d">{{ d.value | fmtdt }}</template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import Chart from 'chart.js'
import downtimeService from '@/services/downtimeService'

const COLORS = {
  'hrm-api':         { border: 'rgba(54,162,235,1)',  bg: 'rgba(54,162,235,.12)' },
  'admin-api':       { border: 'rgba(75,192,192,1)',   bg: 'rgba(75,192,192,.12)' },
  'billing-service': { border: 'rgba(255,159,64,1)',   bg: 'rgba(255,159,64,.12)' },
}

export default {
  name: 'LiveMonitoring',
  filters: {
    fmtdt: v => v ? new Date(v).toLocaleString() : '—'
  },
  data() {
    return {
      loading: false,
      lastRefreshed: null,
      services: [],
      recentEvents: [],
      trend: {},
      countdown: 30,
      chartInst: null,
      timer: null,
      ticker: null,
      fields: [
        { key: 'service_name',    label: 'Service',     sortable: true },
        { key: 'status',          label: 'Status',      sortable: true },
        { key: 'response_time_ms',label: 'Response ms', sortable: true },
        { key: 'entry_type',      label: 'Type',        sortable: true },
        { key: 'checked_at',      label: 'Checked At',  sortable: true },
      ]
    }
  },
  created() {
    this.load()
    this.timer  = setInterval(this.load, 30000)
    this.ticker = setInterval(() => {
      this.countdown = this.countdown > 0 ? this.countdown - 1 : 30
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    clearInterval(this.ticker)
    if (this.chartInst) this.chartInst.destroy()
  },
  methods: {
    load() {
      this.loading = true
      this.countdown = 30
      downtimeService.getLive(
        data => {
          this.services     = data.services     || []
          this.recentEvents = data.recentEvents || []
          this.trend        = data.trend        || {}
          this.lastRefreshed = new Date().toLocaleString()
          this.loading = false
          this.$nextTick(this.renderChart)
        },
        err => {
          this.loading = false
          this.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    renderChart() {
      const ctx = this.$refs.chart
      if (!ctx) return
      const datasets = Object.keys(this.trend).map(svc => ({
        label: svc,
        data: (this.trend[svc] || []).map(p => ({ x: p.checked_at, y: p.response_time_ms })),
        borderColor:     (COLORS[svc] || {}).border || 'rgba(150,150,150,1)',
        backgroundColor: (COLORS[svc] || {}).bg     || 'rgba(150,150,150,.1)',
        fill: true, tension: 0.3, pointRadius: 2,
      }))
      if (this.chartInst) {
        this.chartInst.data.datasets = datasets
        this.chartInst.update()
        return
      }
      this.chartInst = new Chart(ctx, {
        type: 'line',
        data: { datasets },
        options: {
          responsive: true, maintainAspectRatio: true,
          scales: {
            xAxes: [{ type: 'time', time: { unit: 'hour' }, ticks: { maxTicksLimit: 12 } }],
            yAxes: [{ ticks: { beginAtZero: true, callback: v => v + ' ms' } }]
          },
          legend: { position: 'top' }
        }
      })
    },
    badgeVariant(s) {
      return s === 'UP' ? 'success' : s === 'DOWN' ? 'danger' : s === 'DEGRADED' ? 'warning' : 'secondary'
    },
    statusIcon(s) {
      return s === 'UP' ? 'icon-check' : s === 'DOWN' ? 'icon-close' : 'icon-exclamation'
    }
  }
}
</script>

<style scoped>
.status-card { transition: box-shadow .2s; }
.status-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.1); }
</style>
